import React, { ReactNode } from 'react';
import moment from 'moment';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { withRouter } from 'react-router-dom';
import _get from 'lodash.get';
import uuid from 'uuid';
import { Button, Col, Row } from 'antd';
import { Formik, FormikState, Field } from 'formik';
import {
  DatePicker,
  InputNumber,
  handleSubmit,
  toCurrency
} from '@while-and-for/forms';
import { Form, SelectBrands, SelectCountries } from 'forms/components';
import { Loading, ErrorComponent } from '@while-and-for/components';

import { GET_METRICS } from 'graphql/queries';
import { GET_METRIC_BY_ID, ADD_METRICS, onError, onSuccess } from './requests';

import FormSchema from './FormSchema';
import { Values, Props, initialValues } from './types';

const fromCurrency = (b: string): string | undefined =>
  b.replace(/(€|\$)\s?|(,*)/g, '');

const toPercentage = (c: number): string => {
  if (isNaN(c)) return '';
  return c ? `${c} %` : '';
};
const fromPercentage = (d: string): string | undefined => d.replace(' %', '');

const CoreMetricsForm: React.FC<Props> = ({ history, id }) => {
  const { data, loading, error } = useQuery(GET_METRIC_BY_ID, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only'
  });

  const [updateMetrics] = useMutation(ADD_METRICS, {
    refetchQueries: [{ query: GET_METRICS, variables: { filter: {} } }]
  });

  if (loading) return <Loading />;
  if (error) return <ErrorComponent error={error} />;

  const { date, ...metric } = _get(data, 'metrics[0]', {});
  // Remove unused vars
  delete metric.__typename;
  return (
    <Formik
      validationSchema={FormSchema}
      onSubmit={handleSubmit({
        onError,
        onSuccess: onSuccess(history),
        submit: (values: Values): void => {
          updateMetrics({
            variables: values
          });
        }
      })}
      initialValues={{
        ...(id ? metric : initialValues),
        id: id || uuid.v4(),
        date: date ? moment(date) : moment()
      }}
    >
      {({ isSubmitting }: FormikState<Values>): ReactNode => (
        <Form>
          <Row gutter={8}>
            <Col span={12}>
              <Field
                required
                label="Country"
                name="country_id"
                component={SelectCountries}
                placeholder="Select a country"
              />
            </Col>
            <Col span={12}>
              <Field
                required
                label="Brand"
                name="brand_id"
                component={SelectBrands}
                placeholder="Select a brand"
              />
            </Col>
            <Col span={12}>
              <Field
                label="CNS (Budget)"
                name="cns_budget"
                component={InputNumber}
                placeholder="Enter CNS (Budget)"
                formatter={toCurrency('€')}
                parser={fromCurrency}
              />
            </Col>
            <Col span={12}>
              <Field
                label="CNS (LE)"
                name="cns_le"
                component={InputNumber}
                placeholder="Enter CNS (LE)"
                formatter={toCurrency('€')}
                parser={fromCurrency}
              />
            </Col>
            <Col span={12}>
              <Field
                required
                label="Sales Volume (Budget)"
                name="sales_volume_budget"
                component={InputNumber}
                placeholder="Enter a volume (Budget)"
                formatter={toCurrency('')}
                parser={fromCurrency}
              />
            </Col>
            <Col span={12}>
              <Field
                required
                label="Sales Volume (LE)"
                name="sales_volume_le"
                component={InputNumber}
                placeholder="Enter a volume (LE)"
                formatter={toCurrency('')}
                parser={fromCurrency}
              />
            </Col>
            <Col span={12}>
              <Field
                label="Gross Margin"
                name="gross_margin"
                component={InputNumber}
                placeholder="Enter Gross Margin"
                min={0}
                max={100}
                formatter={toPercentage}
                parser={fromPercentage}
              />
            </Col>
            <Col span={12}>
              <Field
                label="Gross Margin Ratio"
                name="gross_margin_ratio"
                component={InputNumber}
                placeholder="Enter Gross Margin Ratio"
                min={0}
                max={100}
                formatter={toPercentage}
                parser={fromPercentage}
              />
            </Col>
            <Col span={12}>
              <Field
                label="A&P"
                name="a_and_p"
                component={InputNumber}
                placeholder="Enter A&P"
                formatter={toCurrency('€')}
                parser={fromCurrency}
              />
            </Col>
            <Col span={12}>
              <Field
                label="A&P % CNS"
                name="a_and_p_cns"
                component={InputNumber}
                placeholder="Enter A&P % CNS"
                min={0}
                max={100}
                formatter={toPercentage}
                parser={fromPercentage}
              />
            </Col>
            <Col span={12}>
              <Field
                label="Net Contribution"
                name="net_contribution"
                component={InputNumber}
                placeholder="Enter Net Contribution"
                formatter={toCurrency('€')}
                parser={fromCurrency}
              />
            </Col>
            <Col span={12}>
              <Field
                label="COP"
                name="cop"
                component={InputNumber}
                placeholder="Enter % COP"
                formatter={toCurrency('€')}
                parser={fromCurrency}
              />
            </Col>
            <Col span={12}>
              <Field
                label="% Cognac"
                name="percent_cognac"
                component={InputNumber}
                placeholder="Enter % cognac"
                min={0}
                max={100}
                formatter={toPercentage}
                parser={fromPercentage}
              />
            </Col>
            <Col span={12}>
              <Field
                label="Head Count"
                name="head_count"
                component={InputNumber}
                placeholder="Enter a head count"
                formatter={toCurrency('')}
                parser={fromCurrency}
                s
              />
            </Col>
            <Col span={12}>
              <Field
                required
                label="Date"
                name="date"
                component={DatePicker}
                placeholder="Select a date"
              />
            </Col>
          </Row>
          <Button
            block
            type="primary"
            htmlType="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Submit
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default withRouter(CoreMetricsForm);
